import {FIELD_CLOUDS} from '../../configs/fieldAmbience';

/**
 * Облака над пейзажем и полем. Чистые функции без Phaser и DOM — по ним
 * облака каждый кадр двигает и красит сцена (см. scenes/FieldAmbience).
 *
 * Облака плывут все в одну сторону и почти с одной скоростью: ветер на весь
 * матч один (см. resolveCloudDrift). Плывут они через весь пейзаж, и над
 * игровым полем тоже:
 *  - у края картинки пейзажа облако проявляется постепенно (картинка там
 *    уходит в черный, и белое облако на черном смотрелось бы наклейкой);
 *  - над игровым полем облако видно, но прозрачнее, чем над пейзажем: отряды
 *    и строения под ним должны читаться (см. FIELD_CLOUDS.overField).
 *
 * «Насколько точка внутри поля» отвечает карта покрытия (buildFieldCoverage):
 * поле не прямоугольник, и по рамке вокруг него облака светлели бы над
 * пустыми углами.
 *
 * ВЬЮГА ГОНИТ ОБЛАКА (зимние подложки, числа — FIELD_CLOUDS.storm). Пока над
 * полем идет снежный заряд, облака летят быстрее — посередине между своей
 * обычной скоростью и скоростью его снега, — и их становится больше. Разгон
 * идет за одним числом — «насколько вьюга владеет облаками» (0 — ее нет, 1 —
 * в разгаре; advanceCloudStormLevel, resolveCloudStormSpeed). Лишние облака
 * ведет advanceStormClouds: они приходят из-за наветренного края потоком, по
 * одному через равные промежутки (resolveStormCloudGap), пока заряд не начал
 * стихать. Без вьюги все здесь считается ровно так, как считалось до нее.
 *
 * В состояние партии случайность не попадает: антураж целиком визуальный, у
 * каждого клиента свой.
 */

const randomRange = (rng, min, max) => min + rng() * (max - min);
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const clamp01 = (value) => Math.min(1, Math.max(0, value));

const smoothstep = (from, to, value) => {
  if (to <= from) {
    return value >= to ? 1 : 0;
  }
  const share = clamp01((value - from) / (to - from));
  return share * share * (3 - 2 * share);
};

// FNV-1a: по строке — число, одно и то же у всех клиентов и после перезагрузки
const hashString = (text) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/**
 * Ветер матча: в какую сторону и с каким наклоном плывут облака. Считается из
 * признака партии, поэтому не меняется ни при перерисовке поля, ни после
 * перезагрузки страницы.
 *
 * @param {string|number} seed — признак партии
 * @returns {{direction: number, tiltRad: number}} direction: 1 — слева
 *   направо, -1 — справа налево
 */
export const resolveCloudDrift = (seed) => {
  const hash = hashString(String(seed ?? ''));
  // Младший бит у FNV слабый — берем биты из середины
  const direction = (hash >>> 7) & 1 ? 1 : -1;
  const tiltShare = ((hash >>> 12) % 2001) / 1000 - 1;

  return {direction, tiltRad: toRadians(FIELD_CLOUDS.driftTiltDeg * tiltShare)};
};

const blurRows = (source, columns, rows, radius) => {
  const target = new Float32Array(source.length);
  for (let row = 0; row < rows; row += 1) {
    const offset = row * columns;
    let sum = 0;
    for (let column = -radius; column <= radius; column += 1) {
      sum += column >= 0 && column < columns ? source[offset + column] : 0;
    }
    for (let column = 0; column < columns; column += 1) {
      target[offset + column] = sum / (radius * 2 + 1);
      const leaving = column - radius;
      const entering = column + radius + 1;
      sum += (entering < columns ? source[offset + entering] : 0) - (leaving >= 0 ? source[offset + leaving] : 0);
    }
  }
  return target;
};

const blurColumns = (source, columns, rows, radius) => {
  const target = new Float32Array(source.length);
  for (let column = 0; column < columns; column += 1) {
    let sum = 0;
    for (let row = -radius; row <= radius; row += 1) {
      sum += row >= 0 && row < rows ? source[row * columns + column] : 0;
    }
    for (let row = 0; row < rows; row += 1) {
      target[row * columns + column] = sum / (radius * 2 + 1);
      const leaving = row - radius;
      const entering = row + radius + 1;
      sum += (entering < rows ? source[entering * columns + column] : 0)
        - (leaving >= 0 ? source[leaving * columns + column] : 0);
    }
  }
  return target;
};

/**
 * Карта покрытия: насколько точка пейзажа внутри игрового поля. 0 — далеко
 * снаружи, около 0.5 — на кромке поля, 1 — в его глубине. Переход от 0 к 1
 * занимает полосу шириной в две глубины (FIELD_CLOUDS.overField.depthFactor).
 *
 * Устройство: сетка клеток поверх пейзажа, клетки под ячейками поля закрашены
 * единицей, остальные нулем; затем сетка размыта скользящим средним. Считается
 * один раз на поле, а читается каждый кадр — по четырем соседним клеткам.
 *
 * @param {Object} params
 * @param {Array<{x: number, y: number}>} params.cellCenters — центры ячеек
 *   игрового поля (без моря)
 * @param {number} params.hexSize
 * @param {{left: number, right: number, top: number, bottom: number}} params.rect —
 *   прямоугольник пейзажа
 * @returns {{sample: Function}} sample(x, y) -> 0..1
 */
export const buildFieldCoverage = ({cellCenters, hexSize, rect} = {}) => {
  if (!rect || !(hexSize > 0) || !(rect.right > rect.left) || !(rect.bottom > rect.top)) {
    return {sample: () => 0};
  }

  const step = hexSize * FIELD_CLOUDS.overField.gridStepFactor;
  const columns = Math.max(1, Math.ceil((rect.right - rect.left) / step));
  const rows = Math.max(1, Math.ceil((rect.bottom - rect.top) / step));
  const occupancy = new Float32Array(columns * rows);

  // Ячейка закрашивает клетки в радиусе своей описанной окружности: соседние
  // ячейки смыкаются без дыр
  (cellCenters || []).forEach(({x, y}) => {
    const fromColumn = Math.max(0, Math.floor((x - hexSize - rect.left) / step));
    const toColumn = Math.min(columns - 1, Math.floor((x + hexSize - rect.left) / step));
    const fromRow = Math.max(0, Math.floor((y - hexSize - rect.top) / step));
    const toRow = Math.min(rows - 1, Math.floor((y + hexSize - rect.top) / step));
    for (let row = fromRow; row <= toRow; row += 1) {
      for (let column = fromColumn; column <= toColumn; column += 1) {
        const cellX = rect.left + (column + 0.5) * step;
        const cellY = rect.top + (row + 0.5) * step;
        if (Math.hypot(cellX - x, cellY - y) <= hexSize) {
          occupancy[row * columns + column] = 1;
        }
      }
    }
  });

  const blurRadius = Math.max(1, Math.round((hexSize * FIELD_CLOUDS.overField.depthFactor) / step));
  const coverage = blurColumns(blurRows(occupancy, columns, rows, blurRadius), columns, rows, blurRadius);
  const valueAt = (column, row) => (
    column >= 0 && column < columns && row >= 0 && row < rows ? coverage[row * columns + column] : 0
  );

  return {
    sample: (x, y) => {
      // Значения клеток относятся к их центрам
      const gridX = (x - rect.left) / step - 0.5;
      const gridY = (y - rect.top) / step - 0.5;
      const column = Math.floor(gridX);
      const row = Math.floor(gridY);
      const shareX = gridX - column;
      const shareY = gridY - row;
      const top = valueAt(column, row) * (1 - shareX) + valueAt(column + 1, row) * shareX;
      const bottom = valueAt(column, row + 1) * (1 - shareX) + valueAt(column + 1, row + 1) * shareX;
      return top * (1 - shareY) + bottom * shareY;
    },
  };
};

/**
 * Видимость у края картинки пейзажа: 0 — у самого края (картинка там ушла в
 * черный), 1 — в глубине
 */
export const resolveGroundEdgeFade = (rect, x, y) => {
  const side = Math.min(rect.right - rect.left, rect.bottom - rect.top);
  const distance = Math.min(x - rect.left, rect.right - x, y - rect.top, rect.bottom - y);
  return smoothstep(side * FIELD_CLOUDS.edgeFade.fromShare, side * FIELD_CLOUDS.edgeFade.toShare, distance);
};

// Точки пятна облака, по которым смотрится покрытие поля: центр и восемь точек
// по контуру видимой части картинки (в долях полуразмеров пятна)
const FOOTPRINT_POINTS = [
  [0, 0], [1, 0], [-1, 0], [0, 1], [0, -1], [0.7, 0.7], [-0.7, 0.7], [0.7, -0.7], [-0.7, -0.7],
];

/**
 * Насколько облако зашло на игровое поле: наибольшее значение карты покрытия
 * под его пятном. По центру судить нельзя: облако шириной в несколько ячеек
 * накрывает край поля, когда его центр еще далеко снаружи.
 *
 * @param {{sample: Function}} coverage — карта покрытия поля
 * @param {{x: number, y: number, width: number}} cloud
 * @returns {number} 0..1
 */
export const resolveCloudFieldCoverage = (coverage, cloud) => {
  const reachX = cloud.width * FIELD_CLOUDS.overField.reachShare.x;
  const reachY = cloud.width * FIELD_CLOUDS.overField.reachShare.y;
  let largest = 0;
  for (let index = 0; index < FOOTPRINT_POINTS.length; index += 1) {
    const [shareX, shareY] = FOOTPRINT_POINTS[index];
    largest = Math.max(largest, coverage.sample(cloud.x + shareX * reachX, cloud.y + shareY * reachY));
  }
  return largest;
};

/**
 * Плотность облака над игровым полем: 1 — над пейзажем, над полем — доля из
 * конфига (FIELD_CLOUDS.overField.alphaFactor). Облако над полем остается
 * видимым, но сквозь него читаются отряды и строения.
 *
 * @param {number} fieldCoverage — покрытие поля под облаком (см.
 *   resolveCloudFieldCoverage)
 * @returns {number} множитель прозрачности
 */
export const resolveCloudFieldFactor = (fieldCoverage) => {
  const {alphaFactor, from, to} = FIELD_CLOUDS.overField;
  return 1 - (1 - alphaFactor) * smoothstep(from, to, fieldCoverage);
};

/** Скорость облака по осям, единиц поля в секунду */
const resolveCloudVelocity = (cloud, drift) => ({
  x: drift.direction * cloud.speed * Math.cos(drift.tiltRad),
  y: cloud.speed * Math.sin(drift.tiltRad),
});

/** Высота кадра облака: картинки вытянуты по горизонтали */
export const resolveCloudHeight = (cloud) => cloud.width * FIELD_CLOUDS.aspect;

/**
 * Обычная скорость облаков над этим пейзажем, единиц поля в секунду: у каждого
 * облака она своя, с небольшим разбросом вокруг этой. По ней же считается
 * ход потока облаков вьюги (advanceStormClouds).
 *
 * @param {{left: number, right: number}} rect — прямоугольник пейзажа
 * @param {number} [hexSize] — размер ячейки: по нему скорость ограничена на
 *   огромных полях
 * @returns {number}
 */
export const resolveCloudBaseSpeed = (rect, hexSize) => {
  // На огромном поле пейзаж растянут в разы: облако «за столько-то секунд
  // через пейзаж» неслось бы со скоростью птицы — здесь потолок в ячейках
  const speedLimit = hexSize > 0 ? hexSize * FIELD_CLOUDS.maxSpeedFactor : Infinity;
  return Math.min((rect.right - rect.left) / FIELD_CLOUDS.crossingSeconds, speedLimit);
};

/**
 * Картинка для нового облака: такая, какой над пейзажем сейчас нет. Одинаковые
 * облака рядом сразу бросаются в глаза, поэтому повтор берется, только когда
 * картинок меньше, чем облаков, — и тогда та, что встречается реже всех.
 * Прежнюю картинку самого облака стараемся не брать: иначе на место ушедшего
 * приплывало бы такое же.
 *
 * @param {Object} params
 * @param {Function} params.rng
 * @param {number} params.variantCount — сколько есть картинок облаков
 * @param {Array<number|undefined>} params.usedVariants — картинки остальных
 *   облаков над пейзажем
 * @param {number} [params.previousVariant] — картинка облака, которое заменяют
 * @returns {number}
 */
export const pickCloudVariant = ({rng, variantCount, usedVariants, previousVariant}) => {
  const uses = new Array(variantCount).fill(0);
  (usedVariants || []).forEach((variant) => {
    if (Number.isInteger(variant) && variant >= 0 && variant < variantCount) {
      uses[variant] += 1;
    }
  });
  const fewestUses = Math.min(...uses);
  const rarest = uses.map((count, variant) => variant).filter((variant) => uses[variant] === fewestUses);
  const fresh = rarest.filter((variant) => variant !== previousVariant);
  const pool = fresh.length > 0 ? fresh : rarest;

  return pool[Math.min(pool.length - 1, Math.floor(rng() * pool.length))];
};

/** Облако со случайным видом; место и картинку ему назначает вызывающий */
const createCloud = ({rect, hexSize, rng, variant}) => {
  const side = rect.right - rect.left;
  // На огромном поле пейзаж растянут в разы: облако «в долю пейзажа» вышло бы
  // во весь экран — здесь потолок в ячейках (у скорости он свой, см.
  // resolveCloudBaseSpeed)
  const widthLimit = hexSize > 0 ? hexSize * FIELD_CLOUDS.maxWidthFactor : Infinity;
  const baseSpeed = resolveCloudBaseSpeed(rect, hexSize);

  return {
    x: 0,
    y: 0,
    width: Math.min(side * randomRange(rng, FIELD_CLOUDS.widthShare.min, FIELD_CLOUDS.widthShare.max), widthLimit),
    speed: baseSpeed * (1 + randomRange(rng, -FIELD_CLOUDS.speedSpreadShare, FIELD_CLOUDS.speedSpreadShare)),
    variant,
    // Картинку облака не зеркалим: свет на ней нарисован с той же стороны,
    // с какой от облака отодвинута тень
    rotation: toRadians(randomRange(rng, -FIELD_CLOUDS.rotationDeg, FIELD_CLOUDS.rotationDeg)),
    alpha: randomRange(rng, FIELD_CLOUDS.alpha.min, FIELD_CLOUDS.alpha.max),
  };
};

/**
 * Облака на старте: разложены по всему пейзажу, а не выплывают все разом из-за
 * края. Раскладка — «латинским квадратом»: пейзаж поделен на столько же
 * столбцов и строк, сколько облаков, и в каждом столбце и каждой строке ровно
 * одно облако. Комков и пустых половин не бывает, а два старта подряд все равно
 * не похожи. Картинки облаков не повторяются, пока их хватает (см.
 * pickCloudVariant).
 *
 * @param {Object} params
 * @param {{left: number, right: number, top: number, bottom: number}} params.rect
 * @param {number} [params.hexSize] — размер ячейки: по нему ограничены ширина
 *   и скорость облаков на огромных полях
 * @param {Function} [params.rng]
 * @param {number} [params.variantCount] — сколько есть картинок облаков
 * @param {number} [params.count]
 * @returns {Object[]} облака
 */
export const buildCloudLayout = ({
  rect, hexSize, rng = Math.random, variantCount = 1, count = FIELD_CLOUDS.count,
} = {}) => {
  if (!rect || !(rect.right > rect.left) || !(rect.bottom > rect.top) || !(count > 0) || !(variantCount > 0)) {
    return [];
  }

  // Строки перемешаны: Фишер — Йетс
  const lanes = Array.from({length: count}, (_, index) => index);
  for (let index = lanes.length - 1; index > 0; index -= 1) {
    const other = Math.min(index, Math.floor(rng() * (index + 1)));
    [lanes[index], lanes[other]] = [lanes[other], lanes[index]];
  }

  const width = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  const usedVariants = [];
  return lanes.map((lane, column) => {
    const variant = pickCloudVariant({rng, variantCount, usedVariants});
    usedVariants.push(variant);
    const cloud = createCloud({rect, hexSize, rng, variant});
    cloud.x = rect.left + ((column + randomRange(rng, 0.15, 0.85)) / count) * width;
    cloud.y = rect.top + ((lane + randomRange(rng, 0.15, 0.85)) / count) * height;
    return cloud;
  });
};

/** Ушло ли облако за край пейзажа, к которому его несет ветер */
const checkIfCloudGone = (cloud, rect, velocity) => {
  const halfWidth = cloud.width / 2;
  const halfHeight = resolveCloudHeight(cloud) / 2;
  if (velocity.x > 0 ? cloud.x - halfWidth > rect.right : cloud.x + halfWidth < rect.left) {
    return true;
  }
  if (velocity.y > 0) {
    return cloud.y - halfHeight > rect.bottom;
  }
  return velocity.y < 0 && cloud.y + halfHeight < rect.top;
};

/**
 * Новое облако на место ушедшего: появляется за краем, от которого дует ветер.
 * При наклонном ветре часть облаков приходит сверху или снизу — в доле, в
 * какой ветер несет их через эти края: иначе угол пейзажа с наветренной
 * стороны со временем пустел бы.
 *
 * Место вдоль края выбирается из нескольких случайных — самое далекое от
 * остальных облаков: новое не всплывает вплотную к соседу. Крайние полосы
 * пейзажа (где картинка уходит в черный и облака едва видны) не берутся:
 * «самое просторное место» то и дело оказывалось бы именно там.
 */
const respawnCloud = (cloud, {rect, hexSize, drift, rng, variantCount, clouds}) => {
  const variant = pickCloudVariant({
    rng,
    variantCount,
    usedVariants: clouds.filter((other) => other !== cloud).map((other) => other.variant),
    previousVariant: cloud.variant,
  });
  Object.assign(cloud, createCloud({rect, hexSize, rng, variant}));
  const velocity = resolveCloudVelocity(cloud, drift);
  const width = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  const halfWidth = cloud.width / 2;
  const halfHeight = resolveCloudHeight(cloud) / 2;
  const bandShare = FIELD_CLOUDS.edgeFade.toShare / 2;

  const sideFlow = Math.abs(velocity.x) * height;
  const verticalFlow = Math.abs(velocity.y) * width;
  const entersFromSide = rng() * (sideFlow + verticalFlow) < sideFlow;

  const candidates = [];
  for (let attempt = 0; attempt < FIELD_CLOUDS.respawnAttempts; attempt += 1) {
    candidates.push(entersFromSide
      ? {
        x: velocity.x > 0 ? rect.left - halfWidth : rect.right + halfWidth,
        y: randomRange(rng, rect.top + height * bandShare, rect.bottom - height * bandShare),
      }
      : {
        x: randomRange(rng, rect.left + width * bandShare, rect.right - width * bandShare),
        y: velocity.y > 0 ? rect.top - halfHeight : rect.bottom + halfHeight,
      });
  }

  const distanceToNearest = (place) => clouds.reduce((nearest, other) => (
    other === cloud ? nearest : Math.min(nearest, Math.hypot(other.x - place.x, other.y - place.y))
  ), Infinity);
  const place = candidates.reduce((best, candidate) => (
    distanceToNearest(candidate) > distanceToNearest(best) ? candidate : best
  ));
  cloud.x = place.x;
  cloud.y = place.y;
};

// Ни одно облако не заменено: общий пустой список, чтобы не заводить новый
// массив на каждом кадре
const NO_CLOUDS = Object.freeze([]);

// Вьюги нет: облака плывут своим обычным ходом
const NO_STORM = Object.freeze({level: 0, streamSpeed: 0, isWaning: false});

const clampStormLevel = (value) => (Number.isFinite(value) ? clamp01(value) : 0);

/**
 * Скорость облака во вьюгу: от его обычной скорости (вьюги нет) до середины
 * между нею и скоростью потока снега (заряд в разгаре). Владелец игры попросил
 * именно середину: наравне со снегом облака летят слишком быстро (доля пути до
 * скорости снега — FIELD_CLOUDS.storm.speedBlendShare). Свой небольшой разброс
 * скоростей при этом сжимается: ветер несет все облака почти одинаково, и
 * поток облаков вьюги не сбивается в комки. Медленнее обычного вьюга облако
 * не делает.
 *
 * @param {number} ownSpeed — обычная скорость облака, единиц поля в секунду
 * @param {number} level — насколько вьюга владеет облаками (advanceCloudStormLevel)
 * @param {number} streamSpeed — скорость потока снега в заряде, единиц поля в секунду
 * @returns {number} не меньше обычной скорости облака
 */
export const resolveCloudStormSpeed = (ownSpeed, level, streamSpeed) => {
  const pull = FIELD_CLOUDS.storm.speedBlendShare * clampStormLevel(level);
  return pull > 0 && Number.isFinite(streamSpeed) && streamSpeed > ownSpeed
    ? ownSpeed + (streamSpeed - ownSpeed) * pull
    : ownSpeed;
};

/**
 * Ветер одного шага облаков — для moveCloud: считается раз на шаг, а не на
 * каждое облако. storm — вьюга над облаками: {level, streamSpeed}
 */
const describeWind = (rect, drift, storm) => ({
  rect,
  direction: drift.direction,
  cos: Math.cos(drift.tiltRad),
  sin: Math.sin(drift.tiltRad),
  stormLevel: storm.level,
  streamSpeed: storm.streamSpeed,
});

/**
 * Сдвинуть облако по ветру за seconds секунд.
 *
 * @param {Object} cloud
 * @param {number} seconds
 * @param {Object} wind — ветер этого шага (describeWind)
 * @returns {boolean} ушло ли облако за край пейзажа
 */
const moveCloud = (cloud, seconds, {rect, direction, cos, sin, stormLevel, streamSpeed}) => {
  const speed = resolveCloudStormSpeed(cloud.speed, stormLevel, streamSpeed);
  const velocity = {x: direction * speed * cos, y: speed * sin};
  cloud.x += velocity.x * seconds;
  cloud.y += velocity.y * seconds;
  return checkIfCloudGone(cloud, rect, velocity);
};

/**
 * Шаг облаков на deltaMs: облака сдвигаются по ветру, ушедшие за край
 * заменяются новыми с наветренной стороны. Облака меняются на месте — сцена
 * держит по спрайту на каждое.
 *
 * @param {Object[]} clouds
 * @param {number} deltaMs
 * @param {Object} params
 * @param {{left: number, right: number, top: number, bottom: number}} params.rect
 * @param {{direction: number, tiltRad: number}} params.drift
 * @param {number} [params.hexSize]
 * @param {Function} [params.rng]
 * @param {number} [params.variantCount]
 * @param {{level: number, streamSpeed: number}} [params.storm] — вьюга над
 *   облаками: насколько она ими владеет и скорость потока ее снега (см.
 *   resolveCloudStormSpeed); не задана — обычный ход
 * @param {Object[]} [params.neighbours] — остальные облака над пейзажем, которых
 *   этот шаг не двигает (облака вьюги): новое облако не берет их картинку и не
 *   встает к ним вплотную
 * @returns {Object[]} облака, которые на этом шаге заменены новыми (их спрайтам
 *   нужна новая картинка)
 */
export const advanceClouds = (clouds, deltaMs, {
  rect, drift, hexSize, rng = Math.random, variantCount = 1, storm = NO_STORM, neighbours = NO_CLOUDS,
}) => {
  const seconds = deltaMs / 1000;
  const wind = describeWind(rect, drift, storm);
  let respawned = NO_CLOUDS;
  for (let index = 0; index < clouds.length; index += 1) {
    const cloud = clouds[index];
    if (moveCloud(cloud, seconds, wind)) {
      respawnCloud(cloud, {
        rect, hexSize, drift, rng, variantCount, clouds: neighbours.length > 0 ? [...clouds, ...neighbours] : clouds,
      });
      respawned = respawned === NO_CLOUDS ? [cloud] : [...respawned, cloud];
    }
  }
  return respawned;
};

/**
 * Насколько вьюга владеет облаками после шага времени: 0 — вьюги нет, 1 — она
 * в разгаре. Идет за силой снежного заряда — той же, по которой нарастают и
 * стихают его снег и вой, — но не быстрее, чем от нуля до единицы за
 * FIELD_CLOUDS.storm.rampMs: заряд могут убрать разом, и облака не должны
 * встать рывком.
 *
 * @param {number} level — насколько вьюга владела облаками до шага
 * @param {number} strength — сила заряда сейчас (0 — его нет)
 * @param {number} deltaMs — шаг времени
 * @returns {number} 0..1
 */
export const advanceCloudStormLevel = (level, strength, deltaMs) => {
  const from = clampStormLevel(level);
  const target = clampStormLevel(strength);
  const reach = deltaMs > 0 ? deltaMs / FIELD_CLOUDS.storm.rampMs : 0;
  // До цели рукой подать — ровно в нее: у нуля и единицы не остается хвостов
  // округления (по нулю убираются облака вьюги)
  if (Math.abs(target - from) <= reach) {
    return target;
  }
  return from + Math.sign(target - from) * reach;
};

/**
 * На сколько облаков рассчитан поток облаков вьюги: столько их над пейзажем
 * сверх обычных, когда поток дошел до подветренного края, — всех облаков
 * тогда больше обычного в FIELD_CLOUDS.storm.countFactor раз.
 *
 * @param {number} [regularCount] — сколько над пейзажем обычных облаков
 * @returns {number} целое, не меньше нуля
 */
export const resolveStormCloudCount = (regularCount = FIELD_CLOUDS.count) => {
  const count = Math.round(regularCount * (FIELD_CLOUDS.storm.countFactor - 1));
  return Number.isFinite(count) && count > 0 ? count : 0;
};

/**
 * Промежуток в потоке облаков вьюги, единиц поля: на столько ветер проносит
 * облака между приходами двух соседних. Поток — ровный строй длиной в пейзаж,
 * в котором все облака вьюги (count): за время, пока облако пересекает
 * пейзаж, приходят они все — столько их над пейзажем и держится, сколько бы
 * заряд ни длился (еще одно-два в это время входят в кадр или покидают его).
 * В разгар заряда облака приходят раз в «время пролета над пейзажем / count»
 * секунд; секундами промежуток не задан: поменяйся скорость облаков во вьюгу
 * или размер поля, облаков над пейзажем стало бы вдвое больше или вдвое
 * меньше.
 *
 * @param {{left: number, right: number}} rect — прямоугольник пейзажа
 * @param {number} [count] — на сколько облаков рассчитан поток (resolveStormCloudCount)
 * @returns {number} Infinity — облака вьюги не приходят вовсе
 */
export const resolveStormCloudGap = (rect, count = resolveStormCloudCount()) => {
  const gap = (rect.right - rect.left) / count;
  return gap > 0 && Number.isFinite(gap) ? gap : Infinity;
};

// За шаг с облаками вьюги ничего не случилось: общий ответ, чтобы не заводить
// новый на каждом кадре
const NO_STORM_CLOUD_CHANGES = Object.freeze({arrived: NO_CLOUDS, left: NO_CLOUDS});

/**
 * Шаг облаков вьюги — тех, что прибывают сверх обычных, пока идет снежный
 * заряд. Список меняется на месте:
 *  - облака сдвигаются по ветру, как обычные;
 *  - ушедшее за край уходит насовсем: на его место тут же никто не встает;
 *  - новые приходят ПОТОКОМ: по одному, через почти равные промежутки — из-за
 *    наветренного края, как любое вернувшееся облако: посреди кадра облако не
 *    возникает. Следующее приходит, когда ветер пронес облака на промежуток
 *    потока (resolveStormCloudGap с разбросом
 *    FIELD_CLOUDS.storm.entryJitterShare): пока заряд держится — через равное
 *    время, пока налетает и облака еще медленные — реже. Приходи они и тогда
 *    по часам, у края сбилась бы кучка и вошла бы в кадр разом;
 *  - ни сила заряда, ни уход облака за край приход не торопят: на смену
 *    ушедшему облако приходит в свой черед, а не тут же, и на смену ушедшим
 *    разом — не разом. Приди все облака вьюги разом, пока заряд налетает, и
 *    возвращайся каждое, едва уйдя (так было), в начале вьюги над полем шла
 *    бы их толпа, а посреди нее — редкие вернувшиеся. Сколько облаков вьюги
 *    над пейзажем, задает сам поток: уходит их столько же, сколько приходит;
 *  - заряд стихает (storm.isWaning) — новые не приходят: до поля им уже не
 *    долететь.
 * Облака, оставшиеся над пейзажем, отсюда не убираются: они тают вместе с
 * зарядом, и убирает их сцена.
 *
 * @param {Object[]} stormClouds — облака вьюги
 * @param {number} deltaMs
 * @param {Object} params — как у advanceClouds (rect, drift, hexSize, rng,
 *   variantCount, storm), и еще:
 * @param {boolean} [params.storm.isWaning] — заряд стихает
 * @param {Object[]} [params.regularClouds] — обычные облака над пейзажем: новое
 *   облако вьюги не берет их картинку и не встает к ним вплотную
 * @param {{distanceToEntry: (number|null)}} [params.stream] — ход потока:
 *   сколько ветру осталось пронести облака до прихода следующего (null — поток
 *   еще не начат: первое облако приходит сразу). Меняется на месте. Не
 *   задан — новые облака не приходят
 * @param {number} [params.count] — на сколько облаков рассчитан поток
 *   (resolveStormCloudCount)
 * @returns {{arrived: Object[], left: Object[]}} пришедшие (им нужны спрайты) и
 *   ушедшие насовсем (их спрайты пора убрать)
 */
export const advanceStormClouds = (stormClouds, deltaMs, {
  rect, drift, hexSize, rng = Math.random, variantCount = 1, storm = NO_STORM, regularClouds = NO_CLOUDS,
  stream, count = resolveStormCloudCount(),
}) => {
  const seconds = deltaMs / 1000;
  const wind = describeWind(rect, drift, storm);
  let changes = NO_STORM_CLOUD_CHANGES;
  const note = (kind, cloud) => {
    if (changes === NO_STORM_CLOUD_CHANGES) {
      changes = {arrived: [], left: []};
    }
    changes[kind].push(cloud);
  };

  // С конца: ушедшее вынимается из списка на ходу
  for (let index = stormClouds.length - 1; index >= 0; index -= 1) {
    const cloud = stormClouds[index];
    if (moveCloud(cloud, seconds, wind)) {
      stormClouds.splice(index, 1);
      note('left', cloud);
    }
  }

  const gap = resolveStormCloudGap(rect, count);
  if (!stream || storm.isWaning || !Number.isFinite(gap)) {
    return changes;
  }
  // На сколько ветер пронес облака за шаг: поток идет со скоростью облаков
  const windRun = resolveCloudStormSpeed(resolveCloudBaseSpeed(rect, hexSize), storm.level, storm.streamSpeed)
    * seconds;
  // Поток начинается с облака: чем раньше оно выйдет в путь, тем раньше
  // долетит до кадра
  stream.distanceToEntry = (stream.distanceToEntry ?? 0) - (windRun > 0 ? windRun : 0);
  if (stream.distanceToEntry > 0) {
    return changes;
  }
  // Облако «на место ушедшего», которого не было: встает за наветренным
  // краем — с оглядкой на картинки и места всех облаков над пейзажем
  const cloud = {};
  respawnCloud(cloud, {rect, hexSize, drift, rng, variantCount, clouds: [...regularClouds, ...stormClouds]});
  stormClouds.push(cloud);
  note('arrived', cloud);
  // За шаг — не больше одного облака, и долгов поток не копит: после долгого
  // кадра облака не приходят пачкой
  const {entryJitterShare} = FIELD_CLOUDS.storm;
  stream.distanceToEntry = Math.max(
    0, stream.distanceToEntry + gap * randomRange(rng, 1 - entryJitterShare, 1 + entryJitterShare)
  );
  return changes;
};

/** Где лежит тень облака: чуть ниже и в сторону от него */
export const resolveCloudShadowPlace = (cloud) => ({
  x: cloud.x + cloud.width * FIELD_CLOUDS.shadow.offsetShare.x,
  y: cloud.y + cloud.width * FIELD_CLOUDS.shadow.offsetShare.y,
});
